import { Types } from '@amplitude/analytics-browser';

/**
 * Параметры адреса, значение которых — секрет: `token` ссылки на заявку (по нему подают заявку без входа),
 * `password` фрагмента видеометок OBS, OAuth-параметры согласия AI-клиента. Новый секрет в адресе — сюда же.
 */
export const SENSITIVE_URL_PARAMS = ['token', 'password', 'state', 'code', 'code_challenge'] as const;

export const REDACTED_VALUE = '[redacted]';

const SENSITIVE_PARAM_PATTERN = new RegExp(`(^|[?&#])(${SENSITIVE_URL_PARAMS.join('|')})=[^&#]*`, 'gi');

/** Заменяет значение секретного параметра в адресе или строке запроса: `?token=abc&x=1` → `?token=[redacted]&x=1`. */
export function redactSensitiveUrlParams(value: string): string {
  return value.replace(SENSITIVE_PARAM_PATTERN, `$1$2=${REDACTED_VALUE}`);
}

function redactDeep(value: unknown): unknown {
  if (typeof value === 'string') return redactSensitiveUrlParams(value);
  if (Array.isArray(value)) return value.map(redactDeep);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, nested]) => [key, redactDeep(nested)]));
  }

  return value;
}

/**
 * Autocapture пишет в события полный адрес страницы (`[Amplitude] Page Location`, `[Amplitude] Previous Page
 * Location`), адрес прошлой страницы (`referrer`), а сетевые события — строку запроса (`[Amplitude] URL Query`);
 * готовой настройки, чтобы прятать параметры, у SDK нет — документация предлагает плагин
 * (https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2). Без него токен ссылки на заявку уходил в
 * Amplitude с каждым событием этой страницы и следующей (IMGM-1609).
 *
 * Плагин обязан идти **последним**: адрес и `referrer` дописывают плагины SDK, которые `init` ставит после всех
 * добавленных до него (`analytics-browser/lib/esm/browser-client.js`, шаги autocapture). Поэтому его добавляют
 * после `init` — см. `amplitude.ts`.
 */
export function urlRedactionPlugin(): Types.EnrichmentPlugin {
  return {
    name: 'imgame-url-redaction',
    type: 'enrichment',
    execute: async (event) => ({
      ...event,
      ...(event.event_properties && { event_properties: redactDeep(event.event_properties) as typeof event.event_properties }),
      ...(event.user_properties && { user_properties: redactDeep(event.user_properties) as typeof event.user_properties }),
    }),
  };
}
